import { Icon } from '@blueprintjs/core';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailPanel from 'Components/Generic/DetailPanel';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import VendorData from 'Data/VendorData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { getTagValue, setTagValue } from 'Util/tagUtil';

let DM = new DataManager();

const VendorInformationPanel = ({
  vendorId,
  vendorObject,
  includeCustomerInfo = false,
  title = 'Vendor',
  editable = false,
  hideFax = false,
  hideDrawerButton = false,
  collapsible = true,
}) => {
  const {
    currentObject: vendor,
    hasBeenUpdated,
    reset: resetVendor,
    updateField,
    originalObject: originalVendor,
    doSave,
  } = useUpdatableObject(VendorData.updateRecord, VendorData.createVendor);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalVendor,
    undoFunction: VendorData.updateRecord,
    onUndo: (originalVendor) => fetchVendor(originalVendor.id),
    objectType: 'Vendor',
  });

  useEffect(() => {
    DM.add(
      VendorData.registerListener('BLANK_RECORD_LOADED', onVendorLoaded),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_LOADED', onVendorLoaded),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_CREATED', onVendorCreated),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_UPDATED', onVendorUpdated),
      VendorData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (vendorId && vendorId >= 0) {
      fetchVendor(vendorId);
    }
  }, [vendorId]);

  useEffect(() => {
    if (!editable) {
      resetVendor(vendorObject);
    } else if (vendorObject?.id && Number(vendorObject.id) >= 0) {
      fetchVendor(vendorObject.id);
    }
  }, [vendorObject]);

  const fetchVendor = (id) => {
    VendorData.getRecord(id, {
      _include: 'vendor_notes,addresses',
    });
  };

  const onVendorLoaded = (payload) => {
    if (payload) {
      resetVendor(payload);
      resetUndo(() => fetchVendor(payload.id));
    }
  };

  const onVendorUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  const onVendorCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Vendor Created');
      fetchVendor(payload.id);
    }
  };

  let detailData = [
    {
      label: 'Name',
      value:
        editable || hideDrawerButton ? (
          vendor.name
        ) : (
          <VendorNameWithDrawerButton vendorId={vendor.id} />
        ),
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('name', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Rep',
      value: vendor.contact_full_name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('contact_full_name', newValue);
      },
    },
    {
      label: 'Phone',
      value: vendor.contact_phone_numbers?.phone,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('contact_phone_numbers', {
          ...vendor.contact_phone_numbers,
          phone: newValue,
        });
      },
    },
    ...(!hideFax
      ? [
          {
            label: 'Fax',
            value: vendor.contact_phone_numbers?.fax,
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField('contact_phone_numbers', {
                ...vendor.contact_phone_numbers,
                fax: newValue,
              });
            },
          },
        ]
      : []),
    {
      label: 'Email',
      value: vendor.contact_emails,
      emailFields: ['value'],
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('contact_emails', newValue);
      },
    },
    {
      label: 'Website',
      value: vendor.website_url,
      urlFields: ['value'],
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('website_url', newValue);
      },
    },
    ...(includeCustomerInfo
      ? [
          {
            label: 'Customer Number',
            value: vendor.customer_number,
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField('customer_number', newValue);
            },
          },
        ]
      : []),
    ...(includeCustomerInfo
      ? [
          {
            label: 'Username',
            value: vendor.vendor_website_username,
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField('vendor_website_username', newValue);
            },
          },
        ]
      : []),
    ...(includeCustomerInfo
      ? [
          {
            label: 'Password',
            value: vendor.vendor_website_password,
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField('vendor_website_password', newValue);
            },
          },
        ]
      : []),
    ...(includeCustomerInfo
      ? [
          {
            label: 'Resource Account Id',
            value: getTagValue(vendor, 'resource_account_id'),
            editableFields: ['value'],
            onEdit: (newValue) => {
              updateField(
                'tags',
                setTagValue(vendor, 'resource_account_id', newValue),
              );
            },
          },
        ]
      : []),
    ...(includeCustomerInfo
      ? [
          {
            label: 'Hide From Requests',
            value: (
              <div style={{ display: 'flex' }}>
                {editable && (
                  <input
                    type='checkbox'
                    checked={!!getTagValue(vendor, 'hide_for_requests')}
                    onChange={(event) => {
                      updateField(
                        'tags',
                        setTagValue(
                          vendor,
                          'hide_for_requests',
                          event.target.checked,
                        ),
                      );
                    }}
                  />
                )}
                {!editable && (
                  <Icon
                    size='20'
                    icon={
                      getTagValue(vendor, 'hide_for_requests')
                        ? 'tick'
                        : 'blank'
                    }
                  />
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={() => fetchVendor(vendor.id)} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <DetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible={collapsible}>
      {vendor && <DetailTable data={detailData} editable={editable} />}
    </DetailPanel>
  );
};

VendorInformationPanel.propTypes = {
  vendorId: PropTypes.number,
  vendorObject: PropTypes.object,
  includeCustomerInfo: PropTypes.bool,
  title: PropTypes.string,
  editable: PropTypes.bool,
  hideFax: PropTypes.bool,
  hideDrawerButton: PropTypes.bool,
  collapsible: PropTypes.bool,
};

export default VendorInformationPanel;
